import { useEffect, useRef, useState } from 'react';

/**
 * Fires once when the element first scrolls into view, and never again.
 *
 * Marketing sections use this to gate their entrance animations. In
 * environments without IntersectionObserver (jsdom, SSR) it starts as `true`
 * so the content is visible without needing to scroll.
 */
export function useInView<T extends HTMLElement = HTMLDivElement>(threshold = 0.15) {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(
    () => typeof globalThis.window === 'undefined' || typeof IntersectionObserver === 'undefined'
  );

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}
